"use client";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Alert } from "@/components/ui/alert/Alert";
import { Button } from "@/components/ui/button/Button";
import FormInput from "@/components/ui/form-input";
import TextArea from "@/components/ui/form-input/TextArea";
import SelectDropdown, { type SelectOption } from "@/components/ui/select-dropdown";
import * as AgenticV2API from "@/services/api/agentic-v2";
import { extractApiErrorMessage } from "@/lib/extractApiErrorMessage";

/** Props for {@link RecordLostDialog}. */
export type RecordLostDialogProps = {
  /** Whether the dialog is shown */
  open: boolean;
  /** Close without saving */
  onClose: () => void;
  /** Called after the loss (and optional follow-up) is saved */
  onRecorded: () => void;
  /** Lead (fa_client) id */
  faClientId: string;
  /** Lead display name for the title */
  leadName: string;
};

/**
 * ONB-13 close-as-lost: reason from the catalogue, mandatory detail, optional revisit date.
 * The API stops open work on the record and defaults contact permission from the reason.
 */
export function RecordLostDialog({ open, onClose, onRecorded, faClientId, leadName }: RecordLostDialogProps) {
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [followupDate, setFollowupDate] = useState("");
  const [followupReason, setFollowupReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reasonsQuery = useQuery({
    queryKey: ["onboarding-lost-reasons"],
    queryFn: () => AgenticV2API.getLostReasons(),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const reasonOptions: SelectOption<string>[] = [
    { value: "", label: "Choose a reason" },
    ...(reasonsQuery.data?.items ?? []).map((r) => ({ value: r.name, label: r.name })),
  ];

  const wantsFollowup = followupDate !== "";
  const canSave =
    reason !== "" && detail.trim() !== "" && (!wantsFollowup || followupReason.trim() !== "") && !saving;

  const handleClose = () => {
    setReason("");
    setDetail("");
    setFollowupDate("");
    setFollowupReason("");
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await AgenticV2API.recordLostLead(faClientId, { reason, detail: detail.trim() });
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, "Couldn't record the loss"));
      setSaving(false);
      return;
    }
    onRecorded();
    try {
      if (wantsFollowup) {
        await AgenticV2API.setLostFollowupDate(faClientId, { date: followupDate, reason: followupReason.trim() });
      }
      handleClose();
    } catch {
      // The loss is saved; only the revisit date failed, so don't offer a retry that would record it twice.
      setError("Marked as lost, but the revisit date wasn't saved. Close this dialog and set the revisit date from the lead record.");
      setReason("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} size="lg" data-testid="record-lost-dialog">
      <DialogTitle>Mark {leadName} as lost</DialogTitle>
      <DialogBody>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          This closes their open tasks and any booked meeting. Nothing is sent to the client. You can reinstate them later.
        </p>
        <div className="mt-4 space-y-4">
          <SelectDropdown
            label="Reason *"
            options={reasonOptions}
            value={reason}
            onChange={setReason}
            buttonContainerClassName="mt-2"
            data-testid="record-lost-reason"
          />
          <TextArea
            id="record-lost-detail"
            data-testid="record-lost-detail"
            label="What happened *"
            value={detail}
            onChange={(event) => setDetail(event.target.value)}
            placeholder="A short note for the record"
            rows={3}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormInput
              id="record-lost-followup-date"
              data-testid="record-lost-followup-date"
              type="date"
              label="Revisit on"
              optional
              value={followupDate}
              onChange={(event) => setFollowupDate(event.target.value)}
            />
            <FormInput
              id="record-lost-followup-reason"
              data-testid="record-lost-followup-reason"
              label={wantsFollowup ? "Why revisit *" : "Why revisit"}
              value={followupReason}
              onChange={(event) => setFollowupReason(event.target.value)}
              placeholder="e.g. Pension unlocks next year"
              disabled={!wantsFollowup}
            />
          </div>
        </div>
        {error ? (
          <Alert variant="error" borderless className="mt-4" data-testid="record-lost-error">
            {error}
          </Alert>
        ) : null}
      </DialogBody>
      <DialogActions>
        <Button
          type="button"
          color="red"
          loading={saving}
          disabled={!canSave}
          onClick={() => void handleSave()}
          data-testid="record-lost-submit"
        >
          Mark as lost
        </Button>
        <Button type="button" plain onClick={handleClose} disabled={saving} data-testid="record-lost-cancel">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
